import React, { useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

/**
 * Inner consumer rendered inside <GoogleReCaptchaProvider>.
 *
 * `useGoogleReCaptcha()` must be called from within a Provider tree.
 *
 * NOTE on compatibility: react-google-recaptcha-v3 v1.11.x (the installed
 * version) exposes only `{ executeRecaptcha, container }` from the hook —
 * there is NO `loaded` field (that was added in later versions). Readiness is
 * therefore detected solely by `executeRecaptcha` flipping from undefined to
 * a function. The Provider sets it once the api.js script has loaded and
 * `grecaptcha.ready()` has resolved, so checking for the function is
 * sufficient.
 */
const V3Executor = ({ onReady, onError }) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const lastFnRef = useRef(null);

    useEffect(() => {
        if (typeof executeRecaptcha === "function") {
            if (lastFnRef.current !== executeRecaptcha) {
                lastFnRef.current = executeRecaptcha;
                onReady(executeRecaptcha);
            }
        }
    }, [executeRecaptcha, onReady]);

    // Script load failure detection: the library only console.warns when the
    // api.js <script> tag fails to load. After a grace period, if window.grecaptcha
    // is still missing we surface an error so the submit button can block.
    useEffect(() => {
        if (typeof window !== "undefined" && typeof window.grecaptcha !== "undefined") {
            return;
        }
        const timer = setTimeout(() => {
            if (typeof window.grecaptcha === "undefined") {
                onError(new Error("reCAPTCHA v3 script failed to load"));
            }
        }, 15000);
        return () => clearTimeout(timer);
    }, [onError]);

    return null;
};

/**
 * Presentational wrapper for the reCAPTCHA widgets.
 *
 * Renders:
 *  - v3 : <GoogleReCaptchaProvider> with an inner consumer that exposes
 *         the executeRecaptcha function to the parent hook via onV3Ready.
 *         The v3 badge is rendered by Google at the viewport bottom-right.
 *  - v2 : <ReCAPTCHA> component (checkbox or invisible), ref forwarded via v2Ref.
 *  - inline status messages (load error, init config error, verification
 *    failure, token expired, loading).
 *
 * Props (all provided by useFormRecaptcha):
 *  - v2Ref, v2Config, v3Config
 *  - showV2Widget, showV3Widget
 *  - onV3Ready, onV3Error, onV2Ready, onV2Change, onV2Expired, onV2Errored
 *  - scriptReady, scriptError
 *  - v2Error, v2Expired, v2TokenReady, v2InitError
 *  - style / className : optional container styling
 */
const FormRecaptcha = ({
    v2Ref,
    v2Config,
    v3Config,
    showV2Widget,
    showV3Widget,
    onV3Ready,
    onV3Error,
    onV2Ready,
    onV2Change,
    onV2Expired,
    onV2Errored,
    scriptReady,
    scriptError,
    v2Error,
    v2Expired,
    v2TokenReady,
    v2InitError,
    style,
    className,
}) => {
    // Also render if we have an error to surface, even if widgets are hidden.
    if (!showV2Widget && !showV3Widget && !scriptError && !v2InitError) {
        return null;
    }

    const isInvisible = v2Config?.widget === "invisible";

    return (
        <div
            className={className}
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                ...style,
            }}
        >
            {showV3Widget ? (
                <GoogleReCaptchaProvider
                    reCaptchaKey={v3Config?.siteKey}
                    scriptProps={{
                        async: true,
                        defer: true,
                        appendTo: "head",
                        nonce: undefined,
                    }}
                >
                    <V3Executor onReady={onV3Ready} onError={onV3Error} />
                </GoogleReCaptchaProvider>
            ) : null}

            {showV2Widget ? (
                <ReCAPTCHA
                    ref={v2Ref}
                    sitekey={v2Config?.siteKey}
                    size={isInvisible ? "invisible" : "normal"}
                    onChange={onV2Change}
                    onExpired={onV2Expired}
                    onErrored={onV2Errored}
                    asyncScriptOnLoad={onV2Ready}
                    badge="bottomright"
                />
            ) : null}

            {/* Init-time config error: site key is invalid / wrong type / domain
                not whitelisted. Distinct from scriptError (network load
                failure) and v2Error (post-submit verification failure). */}
            {v2InitError ? (
                <div style={{ color: "#D54941", fontSize: "12px", fontWeight: 500 }}>
                    {v2InitError}
                </div>
            ) : null}

            {scriptError ? (
                <div style={{ color: "#D54941", fontSize: "12px" }}>
                    {scriptError}
                </div>
            ) : null}

            {/* v2Error only fires AFTER the user has attempted submit (the hook
                gates it on v2HasAttempted). It represents a post-execution
                verification failure. */}
            {v2Error ? (
                <div style={{ color: "#D54941", fontSize: "12px" }}>
                    reCAPTCHA verification failed. Please try again.
                </div>
            ) : null}

            {v2Expired ? (
                <div style={{ color: "#D54941", fontSize: "12px" }}>
                    reCAPTCHA expired. Please verify again.
                </div>
            ) : null}

            {(showV2Widget || showV3Widget) && !scriptReady && !scriptError && !v2InitError ? (
                <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                    Loading reCAPTCHA...
                </div>
            ) : null}
        </div>
    );
};

export default FormRecaptcha;
